import { useEffect, useState } from "react";
import {
  Dialog,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  organization_name: "",
};

const CustomPlanModal = ({
  show,
  onClose,
  onSubmit,
  isSubmitting = false,
  defaults = {},
}) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  // Prefill from the logged-in user/org each time the modal opens.
  useEffect(() => {
    if (show) {
      setForm({
        name: defaults.name || "",
        email: defaults.email || "",
        phone: defaults.phone || "",
        organization_name: defaults.organization_name || "",
      });
      setErrors({});
    }
  }, [show, defaults.name, defaults.email, defaults.phone, defaults.organization_name]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required.";
    if (!form.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!form.phone.trim()) {
      nextErrors.phone = "Phone is required.";
    } else if (form.phone.trim().length < 7) {
      nextErrors.phone = "Enter a valid phone number.";
    }
    if (!form.organization_name.trim())
      nextErrors.organization_name = "Organization name is required.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    await onSubmit({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      organization_name: form.organization_name.trim(),
    });
  };

  return (
    <Dialog
      open={show}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
    >
      <div className="pb-0 pr-16 px-6 pt-4">
        <h2 className="text-xl font-bold text-primary">Request a Custom Plan</h2>
        <p className="text-sm text-gray-500 mt-1">
          Submit the form below and our team will reach out with a
          personalized quote.
        </p>
        <IconButton
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ position: "absolute", right: 16, top: 16, color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="pt-6 px-6 flex flex-col gap-4">
          <TextField
            label="Full Name"
            value={form.name}
            onChange={handleChange("name")}
            error={Boolean(errors.name)}
            helperText={errors.name}
            fullWidth
            required
            variant="outlined"
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            error={Boolean(errors.email)}
            helperText={errors.email}
            fullWidth
            required
            variant="outlined"
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={handleChange("phone")}
            error={Boolean(errors.phone)}
            helperText={errors.phone}
            fullWidth
            required
            variant="outlined"
          />
          <TextField
            label="Organization Name"
            value={form.organization_name}
            onChange={handleChange("organization_name")}
            error={Boolean(errors.organization_name)}
            helperText={errors.organization_name}
            fullWidth
            required
            variant="outlined"
          />
        </div>

        <div className="flex justify-end gap-3 px-6 pb-4 pt-6">
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              borderColor: "grey.300",
              color: "text.primary",
              textTransform: "uppercase",
              fontWeight: 600,
              px: 3,
              "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              bgcolor: "#0A2342",
              "&:hover": { bgcolor: "#0A2342" },
              textTransform: "uppercase",
              fontWeight: 600,
              px: 3,
            }}
          >
            {isSubmitting ? "Sending..." : "Send Request"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default CustomPlanModal;
